import {
	AppstoreOutlined,
	DatabaseOutlined,
	DollarOutlined,
	EditOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	PlusOutlined,
	TableOutlined,
} from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, InputNumber, Modal, message, Select, Table } from "antd";
import { type HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { organizationsApi } from "@/modules/organizations/api/organizations.api";
import type { AppRole } from "@/shared/config/navigation";
import { canEditRoomPrice, canManageObjectStructure } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { formatMoney } from "@/shared/utils/money";
import { blocksApi } from "../api/blocks.api";
import { buildingsApi } from "../api/buildings.api";
import { roomsApi } from "../api/rooms.api";
import { sectionsApi } from "../api/sections.api";
import { RoomsFilter } from "../components/RoomsFilter";
import type { Room } from "../types/objects.types";
import { getUploadedAssetUrl } from "../utils/uploaded-asset-url";

export function getBlockSvgUrl(apiUrl: string, svg?: string) {
	return getUploadedAssetUrl(apiUrl, svg);
}

export function RoomsMapPage() {
	const env = getClientEnv();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const [activeMode, setActiveMode] = useState<string>("grid");
	const [bulkPricingOpen, setBulkPricingOpen] = useState(false);
	const [bulkPricingFloor, setBulkPricingFloor] = useState<number | "all">("all");
	const [bulkPricingForm] = Form.useForm();
	const canManageRooms = canManageObjectStructure(role);
	const canChangePrice = canEditRoomPrice(role);
	const shouldChooseOrganization = role === "super";
	const orgIdFromUrl = searchParams.get("orgId") || "";
	const [selectedOrgId, setSelectedOrgId] = useState<string>(orgIdFromUrl);
	const initialBuildingId = searchParams.get("buildingId") || "";
	const initialBlockId = searchParams.get("blockId") || "";

	const getStatusBadgeClass = (status: string) => {
		switch (status) {
			case "sotuvda":
				return "green";
			case "band":
				return "amber";
			case "sotilgan":
				return "slate";
			case "bekor":
				return "red";
			default:
				return "slate";
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status) {
			case "sotuvda":
				return "Sotuvda";
			case "band":
				return "Band qilingan";
			case "sotilgan":
				return "Sotilgan";
			case "bekor":
				return "Bekor qilingan";
			default:
				return status;
		}
	};

	const tableCellLabel = (label: string) => ({
		onCell: () => ({ "data-label": label }) as unknown as HTMLAttributes<HTMLElement>,
	});

	const { data: organizations = [] } = useQuery({
		queryKey: ["organizations", "rooms-list"],
		queryFn: () => organizationsApi.getOrganizations({ limit: 100 }),
		enabled: shouldChooseOrganization,
	});

	const { data: buildingFromUrl } = useQuery({
		queryKey: ["building", initialBuildingId, "rooms-map-org-resolve"],
		queryFn: () => buildingsApi.getBuilding(initialBuildingId),
		enabled: shouldChooseOrganization && !!initialBuildingId && !orgIdFromUrl,
	});

	useEffect(() => {
		if (orgIdFromUrl) {
			setSelectedOrgId(orgIdFromUrl);
		}
	}, [orgIdFromUrl]);

	useEffect(() => {
		if (!(shouldChooseOrganization && buildingFromUrl?.orgId && !orgIdFromUrl)) {
			return;
		}

		setSelectedOrgId(buildingFromUrl.orgId);
		setSearchParams(
			{
				orgId: buildingFromUrl.orgId,
				buildingId: initialBuildingId,
				...(initialBlockId ? { blockId: initialBlockId } : {}),
				sectionId: searchParams.get("sectionId") || "all",
				status: searchParams.get("status") || "all",
				roomsCount: searchParams.get("roomsCount") || "all",
			},
			{ state: location.state, replace: true }
		);
	}, [
		shouldChooseOrganization,
		buildingFromUrl,
		orgIdFromUrl,
		setSearchParams,
		location.state,
		initialBuildingId,
		initialBlockId,
		searchParams,
	]);

	useEffect(() => {
		if (
			shouldChooseOrganization &&
			organizations.length > 0 &&
			!selectedOrgId &&
			!(initialBuildingId && !orgIdFromUrl)
		) {
			setSelectedOrgId(organizations[0].id);
		}
	}, [shouldChooseOrganization, organizations, selectedOrgId, initialBuildingId, orgIdFromUrl]);

	const { data: buildings = [] } = useQuery({
		queryKey: ["buildings", selectedOrgId],
		queryFn: () =>
			buildingsApi.getBuildings({
				limit: 100,
				...(selectedOrgId ? { orgId: selectedOrgId } : {}),
			}),
		enabled: !shouldChooseOrganization || !!selectedOrgId,
		staleTime: 0,
		refetchOnMount: "always",
	});

	// We need blocks for the buildings of the selected organization/context
	const { data: blocks = [] } = useQuery({
		queryKey: ["blocks", selectedOrgId, buildings.map((b) => b.id).join(",")],
		queryFn: async () => {
			if (buildings.length === 0) {
				return [];
			}
			const promises = buildings.map((b) => blocksApi.getBlocks({ buildingId: b.id, limit: 100 }));
			const results = await Promise.all(promises);
			return results.flat();
		},
		enabled: buildings.length > 0,
		staleTime: 0,
		refetchOnMount: "always",
	});

	// We need sections for the blocks of the selected organization/context
	const { data: sections = [] } = useQuery({
		queryKey: ["sections", selectedOrgId, blocks.map((b) => b.id).join(",")],
		queryFn: async () => {
			if (blocks.length === 0) {
				return [];
			}
			const promises = blocks.map((block) =>
				sectionsApi.getSections({ blockId: block.id, limit: 100 })
			);
			const results = await Promise.all(promises);
			return results.flat();
		},
		enabled: blocks.length > 0,
		staleTime: 0,
		refetchOnMount: "always",
	});

	const returnTo = (location.state as any)?.returnTo;

	const handleBack = () => {
		if (returnTo) {
			navigate(returnTo);
		} else {
			navigate("/objects");
		}
	};

	const selectedSectionId = searchParams.get("sectionId") || "all";
	const selectedStatus = searchParams.get("status") || "all";
	const selectedRoomsCount = searchParams.get("roomsCount") || "all";

	// Only use first building/block if we have data
	const selectedBuildingId = initialBuildingId || (buildings.length > 0 ? buildings[0].id : "");
	const defaultBlock =
		blocks.find((b) => b.buildingId === selectedBuildingId) ||
		(blocks.length > 0 ? blocks[0] : null);
	const selectedBlockId = initialBlockId || defaultBlock?.id || "";

	// For rooms, query only rooms belonging to the selected building
	const { data: rooms = [] } = useQuery({
		queryKey: ["rooms", selectedBuildingId],
		queryFn: () => roomsApi.getRooms({ buildingId: selectedBuildingId, limit: 100 }),
		enabled: !!selectedBuildingId && selectedBuildingId !== "",
		staleTime: 0,
		refetchOnMount: "always",
	});

	// Resolve blockId (missing in flat backend list response) and buildingId via sections and blocks
	const mappedRooms = useMemo(() => {
		return rooms.map((room) => {
			const section = sections.find((s) => s.id === room.sectionId);
			const resolvedBlockId = section ? section.blockId : room.blockId;
			const block = blocks.find((b) => b.id === resolvedBlockId);
			const resolvedBuildingId = block ? block.buildingId : room.buildingId || "";
			return {
				...room,
				blockId: resolvedBlockId,
				buildingId: resolvedBuildingId,
			};
		});
	}, [rooms, sections, blocks]);

	const urlStatus = searchParams.get("status");
	const urlBuilding = searchParams.get("buildingId");

	useEffect(() => {
		if (
			urlStatus &&
			urlStatus !== "all" &&
			!urlBuilding &&
			mappedRooms.length > 0 &&
			blocks.length > 0
		) {
			const matching = mappedRooms.find((r) => r.status === urlStatus);
			if (matching?.buildingId && matching?.blockId) {
				// Only set params if the resolved block actually exists in our loaded blocks
				const blockExists = blocks.some((b) => b.id === matching.blockId);
				if (blockExists) {
					setSearchParams(
						{
							orgId: selectedOrgId,
							buildingId: matching.buildingId,
							blockId: matching.blockId,
							sectionId: "all",
							status: urlStatus,
							roomsCount: "all",
						},
						{ state: location.state }
					);
				}
			}
		}
	}, [mappedRooms, urlStatus, urlBuilding, setSearchParams, location.state, selectedOrgId, blocks]);

	// Handlers updating the URL params (which triggers state update)
	const handleBuildingBlockChange = (buildingId: string, blockId: string) => {
		// Don't set params if IDs are empty
		if (!(buildingId && blockId)) {
			return;
		}

		setSearchParams(
			{
				orgId: selectedOrgId,
				buildingId,
				blockId,
				sectionId: "all",
				status: "all",
				roomsCount: "all",
			},
			{ state: location.state }
		);
	};

	const handleSectionChange = (sectionId: string) => {
		setSearchParams(
			{
				orgId: selectedOrgId,
				buildingId: selectedBuildingId,
				blockId: selectedBlockId,
				sectionId,
				status: selectedStatus,
				roomsCount: selectedRoomsCount,
			},
			{ state: location.state }
		);
	};

	const handleStatusChange = (status: string) => {
		setSearchParams(
			{
				orgId: selectedOrgId,
				buildingId: selectedBuildingId,
				blockId: selectedBlockId,
				sectionId: selectedSectionId,
				status,
				roomsCount: selectedRoomsCount,
			},
			{ state: location.state }
		);
	};

	const handleRoomsCountChange = (roomsCount: string) => {
		setSearchParams(
			{
				orgId: selectedOrgId,
				buildingId: selectedBuildingId,
				blockId: selectedBlockId,
				sectionId: selectedSectionId,
				status: selectedStatus,
				roomsCount,
			},
			{ state: location.state }
		);
	};

	const openRoomEdit = (roomId: string) => {
		navigate(`/rooms/${roomId}`, { state: { ...location.state, openEdit: true } });
	};

	// 1. Get block metadata
	const currentBlock = blocks.find((b) => b.id === selectedBlockId);
	const floors = currentBlock ? currentBlock.floorsCount : 9;
	const currentBlockSvgUrl = getBlockSvgUrl(env.VITE_API_URL, currentBlock?.blockSvg);

	// 2. Filter rooms for the grid
	const filteredRooms = useMemo(() => {
		return mappedRooms.filter((room) => {
			if (room.floor < 1 || room.floor > floors) {
				return false;
			}

			// Must match current building and block
			if (room.buildingId !== selectedBuildingId || room.blockId !== selectedBlockId) {
				return false;
			}

			// Filter by section/padezd
			if (selectedSectionId !== "all" && room.sectionId !== selectedSectionId) {
				return false;
			}

			// Filter by status
			if (selectedStatus !== "all" && room.status !== selectedStatus) {
				return false;
			}

			// Filter by rooms count
			if (selectedRoomsCount !== "all" && room.roomsCount.toString() !== selectedRoomsCount) {
				return false;
			}

			return true;
		});
	}, [
		mappedRooms,
		floors,
		selectedBuildingId,
		selectedBlockId,
		selectedSectionId,
		selectedStatus,
		selectedRoomsCount,
	]);

	const availableBulkPricingRooms = useMemo(
		() => filteredRooms.filter((room) => room.status === "sotuvda" || room.status === "bekor"),
		[filteredRooms]
	);
	const bulkPricingRooms = useMemo(
		() =>
			availableBulkPricingRooms.filter(
				(room) => bulkPricingFloor === "all" || room.floor === bulkPricingFloor
			),
		[availableBulkPricingRooms, bulkPricingFloor]
	);
	const bulkPricingMutation = useMutation({
		mutationFn: (payload: {
			pricePerArea: number;
			installmentPricePerArea: number;
			installmentMonths: number;
			minInitialPaymentPercent: number;
		}) =>
			roomsApi.bulkUpdatePricing(
				// Each room's prices derive from the two m2 inputs: cash from pricePerArea,
				// installment from installmentPricePerArea, both x that room's area
				bulkPricingRooms.map((room) => ({
					id: room.id,
					pricePerArea: payload.pricePerArea,
					installmentPrice: Math.round(payload.installmentPricePerArea * (room.area || 0)),
				})),
				{
					installmentMonths: payload.installmentMonths,
					minInitialPaymentPercent: payload.minInitialPaymentPercent,
				}
			),
		onSuccess: (updatedRooms) => {
			message.success(`${updatedRooms.length} ta xona uchun narxlar yangilandi`);
			setBulkPricingOpen(false);
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Xonalar narxini yangilashda xatolik yuz berdi");
		},
	});

	const openBulkPricing = () => {
		if (availableBulkPricingRooms.length === 0) {
			message.warning("Joriy filtrda narx belgilash mumkin bo'lgan xona yo'q.");
			return;
		}

		setBulkPricingFloor("all");
		const firstRoom = availableBulkPricingRooms[0];
		const firstRoomInstallmentPerArea =
			firstRoom.installmentPrice && firstRoom.area
				? Math.round(firstRoom.installmentPrice / firstRoom.area)
				: firstRoom.pricePerArea;
		bulkPricingForm.setFieldsValue({
			pricePerArea: firstRoom.pricePerArea,
			installmentPricePerArea: firstRoomInstallmentPerArea,
			installmentMonths: firstRoom.installmentMonths || 12,
			minInitialPaymentPercent: firstRoom.minInitialPaymentPercent ?? 30,
		});
		setBulkPricingOpen(true);
	};

	const saveBulkPricing = () => {
		if (bulkPricingRooms.length === 0) {
			message.warning("Tanlangan qavatda narx belgilash mumkin bo'lgan xona yo'q.");
			return;
		}

		bulkPricingForm.validateFields().then((values) => {
			bulkPricingMutation.mutate(values);
		});
	};

	const handleBulkPricingFloorChange = (value: number | "all") => {
		setBulkPricingFloor(value);
	};

	// 3. Compute legends summaries (based on current block & section filters, but ignoring status filter itself)
	const legendCounts = useMemo(() => {
		const blockRooms = mappedRooms.filter(
			(r) =>
				r.floor >= 1 &&
				r.floor <= floors &&
				r.buildingId === selectedBuildingId &&
				r.blockId === selectedBlockId &&
				(selectedSectionId === "all" || r.sectionId === selectedSectionId) &&
				(selectedRoomsCount === "all" || r.roomsCount.toString() === selectedRoomsCount)
		);

		return {
			sotuvda: blockRooms.filter((r) => r.status === "sotuvda").length,
			sotilgan: blockRooms.filter((r) => r.status === "sotilgan").length,
			band: blockRooms.filter((r) => r.status === "band").length,
			bekor: blockRooms.filter((r) => r.status === "bekor").length,
		};
	}, [
		mappedRooms,
		floors,
		selectedBuildingId,
		selectedBlockId,
		selectedSectionId,
		selectedRoomsCount,
	]);

	// Group rooms by floor
	const roomsByFloor = useMemo(() => {
		const groups: Record<number, Room[]> = {};
		for (let f = floors; f >= 1; f--) {
			groups[f] = [];
		}

		for (const room of filteredRooms) {
			if (!groups[room.floor]) {
				groups[room.floor] = [];
			}
			groups[room.floor].push(room);
		}

		// Sort rooms within each floor by roomNumber ascending
		for (const f in groups) {
			groups[f].sort((a, b) => +a.roomNumber - +b.roomNumber);
		}

		return groups;
	}, [filteredRooms, floors]);

	return (
		<div className="crm-rooms-map">
			<div className="crm-page-head">
				<div>
					<Button className="crm-back-link" icon={<LeftOutlined />} onClick={handleBack}>
						{returnTo === "/reports/rooms"
							? "Hisobotga qaytish"
							: returnTo === "/dashboard"
								? "Bosh sahifaga qaytish"
								: "Binolar"}
					</Button>
					<h1>Xonalar xaritasi</h1>
					<p style={{ color: "var(--crm-ink-3)" }}>Status bo'yicha xonalar joylashuvi</p>
				</div>
				<div className="crm-rooms-map-actions">
					{shouldChooseOrganization && organizations.length > 0 && (
						<Select
							value={
								organizations.some((org) => org.id === selectedOrgId) ? selectedOrgId : undefined
							}
							onChange={(val) => {
								setSelectedOrgId(val);
								setSearchParams({ orgId: val }, { state: location.state });
							}}
							options={organizations.map((org) => ({
								label: org.name,
								value: org.id,
							}))}
							style={{ minWidth: 200 }}
							placeholder="Tashkilotni tanlang"
						/>
					)}
					<div className="crm-seg">
						<button
							type="button"
							className={activeMode === "grid" ? "active" : ""}
							onClick={() => {
								setActiveMode("grid");
								message.info("Ko'rish rejimi o'zgartirildi: Grid xarita");
							}}
						>
							<AppstoreOutlined style={{ marginRight: "6px" }} /> Grid xarita
						</button>
						<button
							type="button"
							className={activeMode === "svg" ? "active" : ""}
							onClick={() => {
								setActiveMode("svg");
								message.info("Ko'rish rejimi o'zgartirildi: SVG Plan");
							}}
						>
							<DatabaseOutlined style={{ marginRight: "6px" }} /> SVG Plan
						</button>
						<button
							type="button"
							className={activeMode === "table" ? "active" : ""}
							onClick={() => {
								setActiveMode("table");
								message.info("Ko'rish rejimi o'zgartirildi: Jadval ko'rinish");
							}}
						>
							<TableOutlined style={{ marginRight: "6px" }} /> Jadval
						</button>
					</div>
					{canChangePrice ? (
						<Button icon={<DollarOutlined />} onClick={openBulkPricing}>
							Ommaviy narx belgilash
						</Button>
					) : null}
					{canManageRooms ? (
						<Button
							className="crm-primary-btn"
							icon={<PlusOutlined />}
							onClick={() => navigate("/rooms/new", { state: location.state })}
						>
							Xona qo'shish
						</Button>
					) : null}
				</div>
			</div>

			{buildings.length === 0 || blocks.length === 0 ? (
				<div className="crm-card" style={{ marginTop: "16px" }}>
					<div className="crm-card-body" style={{ textAlign: "center", padding: "48px 24px" }}>
						<InfoCircleOutlined
							style={{ fontSize: "32px", color: "var(--crm-ink-3)", marginBottom: "12px" }}
						/>
						<h3 style={{ color: "var(--crm-ink-2)", marginBottom: "8px" }}>
							{buildings.length === 0 ? "Binolar mavjud emas" : "Bloklar mavjud emas"}
						</h3>
						<p style={{ color: "var(--crm-ink-3)", fontSize: "14px" }}>
							{buildings.length === 0
								? "Xonalar xaritasini ko'rish uchun avval bino qo'shing"
								: "Xonalar xaritasini ko'rish uchun avval blok qo'shing"}
						</p>
					</div>
				</div>
			) : (
				<>
					<RoomsFilter
						buildings={buildings}
						blocks={blocks}
						sections={sections}
						selectedBuildingId={selectedBuildingId}
						selectedBlockId={selectedBlockId}
						selectedSectionId={selectedSectionId}
						selectedStatus={selectedStatus}
						selectedRoomsCount={selectedRoomsCount}
						onBuildingBlockChange={handleBuildingBlockChange}
						onSectionChange={handleSectionChange}
						onStatusChange={handleStatusChange}
						onRoomsCountChange={handleRoomsCountChange}
						counts={legendCounts}
					/>

					<div className="crm-card">
						<div className="crm-card-body crm-rooms-map-body">
							{activeMode === "grid" && (
								<div className="crm-roommap">
									{Array.from({ length: floors }, (_, i) => floors - i).map((floorNum) => {
										const floorRooms = roomsByFloor[floorNum] || [];

										return (
											<div className="crm-rmrow" key={floorNum}>
												<div className="crm-rmflr">{floorNum}-qavat</div>
												<div className="crm-rmcells">
													{floorRooms.map((room) => (
														<button
															type="button"
															className={`crm-rm ${room.status} ${room.isHighlighted ? "hl" : ""}`}
															key={room.id}
															onClick={() =>
																navigate(`/rooms/${room.id}`, { state: location.state })
															}
															style={{ textAlign: "left", fontFamily: "inherit" }}
														>
															{canChangePrice ? (
																<button
																	className="crm-rm-edit"
																	onClick={(event) => {
																		event.stopPropagation();
																		openRoomEdit(room.id);
																	}}
																	title="Tahrirlash"
																	type="button"
																>
																	<EditOutlined />
																</button>
															) : null}
															<div className="rn">{room.roomNumber}</div>
															<div className="rmeta">
																{room.roomsCount}-xon · {room.area}m²
															</div>
														</button>
													))}

													{floorRooms.length === 0 && (
														<div
															style={{
																gridColumn: "span 8",
																padding: "8px",
																textAlign: "center",
																color: "var(--crm-ink-3)",
																fontSize: "12px",
																background: "var(--crm-surface-2)",
																borderRadius: "8px",
															}}
														>
															Ushbu qavatda filtrga mos keladigan xonalar topilmadi.
														</div>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}

							{activeMode === "table" && (
								<div className="crm-table-scroll">
									<Table
										dataSource={filteredRooms}
										rowKey="id"
										pagination={{ pageSize: 15 }}
										style={{ width: "100%" }}
										className="crm-custom-table crm-rooms-table"
										columns={[
											{
												title: "Xona raqami",
												dataIndex: "roomNumber",
												key: "roomNumber",
												...tableCellLabel("Xona"),
												render: (val, record) => (
													<button
														type="button"
														onClick={() =>
															navigate(`/rooms/${record.id}`, { state: location.state })
														}
														style={{
															background: "none",
															border: "none",
															color: "var(--crm-primary-700)",
															cursor: "pointer",
															fontWeight: 600,
															padding: 0,
															textDecoration: "underline",
														}}
													>
														{val}-xona
													</button>
												),
											},
											{
												title: "Qavat",
												dataIndex: "floor",
												key: "floor",
												...tableCellLabel("Qavat"),
												render: (val) => `${val}-qavat`,
											},
											{
												title: "Xonalar soni",
												dataIndex: "roomsCount",
												key: "roomsCount",
												...tableCellLabel("Xonalar"),
												render: (val) => `${val}-xonali`,
											},
											{
												title: "Maydon (m²)",
												dataIndex: "area",
												key: "area",
												...tableCellLabel("Maydon"),
												render: (val) => `${val} m²`,
											},
											{
												title: "Asosiy narx",
												dataIndex: "basePrice",
												key: "basePrice",
												...tableCellLabel("Narx"),
												render: (val) => `${formatMoney(val)} UZS`,
											},
											{
												title: "Status",
												dataIndex: "status",
												key: "status",
												...tableCellLabel("Status"),
												render: (val) => (
													<span className={`crm-status-badge ${getStatusBadgeClass(val)}`}>
														<span /> {getStatusLabel(val)}
													</span>
												),
											},
										]}
									/>
								</div>
							)}

							{activeMode === "svg" &&
								(currentBlockSvgUrl ? (
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											width: "100%",
											padding: "16px",
										}}
									>
										<div
											style={{
												border: "1px solid var(--crm-border-color)",
												borderRadius: "8px",
												overflow: "hidden",
												width: "100%",
												maxWidth: "700px",
												background: "#fff",
												padding: "16px",
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
											}}
										>
											<img
												src={currentBlockSvgUrl}
												alt="Interactive SVG Plan"
												style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain" }}
											/>
										</div>
										<p style={{ marginTop: "12px", color: "var(--crm-ink-3)", fontSize: "13px" }}>
											Blok chizmasi: {currentBlock?.name}
										</p>
									</div>
								) : (
									<div style={{ width: "100%", textAlign: "center", padding: "48px 24px" }}>
										<DatabaseOutlined
											style={{
												fontSize: "48px",
												color: "var(--crm-ink-3)",
												marginBottom: "16px",
												opacity: 0.7,
											}}
										/>
										<h3 style={{ color: "var(--crm-ink-2)", marginBottom: "8px" }}>
											SVG Plan yuklanmagan
										</h3>
										<p
											style={{
												color: "var(--crm-ink-3)",
												fontSize: "14px",
												maxWidth: "400px",
												margin: "0 auto",
											}}
										>
											Ushbu blok uchun interaktiv SVG chizma fayli biriktirilmagan. Uni blok
											tahrirlash sahifasida yuklashingiz mumkin.
										</p>
									</div>
								))}
						</div>
					</div>

					<div className="crm-card crm-mt" style={{ marginTop: "16px" }}>
						<div
							className="crm-card-body row center gap-s muted"
							style={{
								display: "flex",
								gap: "8px",
								alignItems: "center",
								color: "var(--crm-ink-2)",
							}}
						>
							<InfoCircleOutlined style={{ color: "var(--crm-primary)" }} />
							<span>
								Xonani bosing — tafsiloti (kartochkasi) ochiladi.{" "}
								<b style={{ color: "var(--crm-amber)" }}>Band</b> — sotuv tranzaksiyasi davomidagi
								vaqtinchalik holat.
							</span>
						</div>
					</div>
				</>
			)}

			<Modal
				title="Ko'rinayotgan xonalar uchun narx belgilash"
				open={bulkPricingOpen}
				onOk={saveBulkPricing}
				onCancel={() => setBulkPricingOpen(false)}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{ className: "crm-primary-btn", loading: bulkPricingMutation.isPending }}
				centered
			>
				<p style={{ color: "var(--crm-ink-3)", marginBottom: 16 }}>
					Tanlangan doiradagi {bulkPricingRooms.length} ta sotuvdagi/bekor xona yangilanadi.
				</p>
				<Form form={bulkPricingForm} layout="vertical">
					<Form.Item label="Qaysi xonalarga qo'llansin">
						<Select
							value={bulkPricingFloor}
							onChange={handleBulkPricingFloorChange}
							options={[
								{
									label: `Barcha ko'rinayotgan xonalar (${availableBulkPricingRooms.length})`,
									value: "all",
								},
								...Array.from({ length: floors }, (_, index) => {
									const floor = index + 1;
									const count = availableBulkPricingRooms.filter(
										(room) => room.floor === floor
									).length;
									return {
										label: `${floor}-qavat (${count})`,
										value: floor,
										disabled: count === 0,
									};
								}),
							]}
						/>
					</Form.Item>
					<Form.Item
						name="pricePerArea"
						label="1 m² narxi — naqd (UZS)"
						extra="Har bir xonaning naqd narxi shu qiymatdan hisoblanadi (1 m² narxi × maydon)."
						rules={[{ required: true, message: "1 m² naqd narxini kiriting!" }]}
					>
						<InputNumber<number>
							min={1}
							style={{ width: "100%" }}
							placeholder="7 500 000"
							formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
							parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
						/>
					</Form.Item>
					<Form.Item
						name="installmentPricePerArea"
						label="1 m² narxi — bo'lib to'lash (UZS)"
						extra="Har bir xonaning bo'lib to'lash narxi shu qiymatdan hisoblanadi (1 m² narxi × maydon)."
						dependencies={["pricePerArea"]}
						rules={[
							{ required: true, message: "1 m² bo'lib to'lash narxini kiriting!" },
							({ getFieldValue }) => ({
								validator: (_, value) => {
									const cashPerArea = Number(getFieldValue("pricePerArea") || 0);
									if (!value || value >= cashPerArea) {
										return Promise.resolve();
									}
									return Promise.reject(
										new Error("Bo'lib to'lash narxi naqd narxidan past bo'lishi mumkin emas")
									);
								},
							}),
						]}
					>
						<InputNumber<number>
							min={1}
							style={{ width: "100%" }}
							placeholder="8 000 000"
							formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
							parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
						/>
					</Form.Item>
					<Form.Item
						name="installmentMonths"
						label="Bo'lib to'lash oylari soni"
						rules={[{ required: true, message: "Oylar sonini kiriting!" }]}
					>
						<InputNumber min={1} max={120} precision={0} style={{ width: "100%" }} />
					</Form.Item>
					<Form.Item
						name="minInitialPaymentPercent"
						label="Boshlang'ich minimal to'lov (%)"
						rules={[{ required: true, message: "Minimal foizni kiriting!" }]}
					>
						<InputNumber min={0} max={100} precision={0} style={{ width: "100%" }} />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}
